// src/pos-app/shell/useOperatorSession.js
//
// Operator session hook (F12a Task 5.1) — per-shift PIN lock/handover for a
// shared POS device/station. The station itself is persistent (see
// StationContext); the PERSON operating it changes at shift handover.
//   - lock()      clears the current operator (UI shows a PIN screen).
//   - unlock(pin) validates the PIN against whoever it belongs to and sets
//                 that person as the new operator — it does NOT require the
//                 PIN to match the previous operator (that's the point of a
//                 handover).
//
// Reuses the existing employee PIN validation (authService.employeeLogin),
// the same call LockScreenPage.js makes, rather than reimplementing it.
//
// `fallbackTenantId` (optional): belt-and-braces — callers pass the tenantId
// persisted in the station's own config (StationContext, set at enrollment)
// so unlock() still works if AuthContext.tenantId is ever empty on an
// unforeseen logout path, rather than failing PIN unlock with an empty
// tenant_id and leaving the station stuck on the lock screen.
import { useCallback, useState } from 'react';
import { authService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const MIN_PIN_LENGTH = 4;

export default function useOperatorSession(fallbackTenantId) {
    const { tenantId: authTenantId } = useAuth();
    const tenantId = authTenantId || fallbackTenantId || '';
    const { t } = useLanguage();

    const [operator, setOperator] = useState(null);
    const [unlocking, setUnlocking] = useState(false);
    const [error, setError] = useState(null);

    const lock = useCallback(() => {
        setOperator(null);
        setError(null);
    }, []);

    const unlock = useCallback(async (pin) => {
        if (!pin || pin.length < MIN_PIN_LENGTH) {
            setError(t('invalid_credentials'));
            return null;
        }

        setUnlocking(true);
        setError(null);
        try {
            const result = await authService.employeeLogin(tenantId, pin);
            const nextOperator = {
                employeeId: result.employee_id,
                name: result.employee_name || result.name || result.username || '',
                role: result.user_role,
                isCashier: result.is_cashier,
                preferredLanguage: result.preferred_language,
            };
            setOperator(nextOperator);
            return nextOperator;
        } catch (err) {
            setOperator(null);
            setError(t('invalid_credentials'));
            return null;
        } finally {
            setUnlocking(false);
        }
    }, [tenantId, t]);

    return {
        operator,
        locked: operator == null,
        unlocking,
        error,
        lock,
        unlock,
    };
}
